<template>
  <div>
    <PageHeader title="Commissions">
      <div class="flex">
        <SecondaryButton v-if="selectedRows.length > 0" @click="markAsPaid">Marquer comme payé</SecondaryButton>
        <div v-else class="flex">
          <SecondaryButton class="mr-2" @click="openInvoicesDownloadModal = true">
            {{ $t("Download") }}
          </SecondaryButton>
          <SecondaryButton @click="bulkTransferFormModalOpen = true">Exporter les virements en attente</SecondaryButton>
        </div>
      </div>
    </PageHeader>
    <InvoicesSummary class="mb-5" :summary="summary" @show-to-remind="showBillsToRemind" />
    <InvoicesFilterBar
      :available-filters="availableFilters"
      :selected-filters="selectedFilters"
      :current-page="currentPage"
      @update-filter="updateSelectedFilters"
      @update-dates="updateDateFilters"
      @update-unpaid-bill="updateUnpaidBillFilter"
      @clear-filters="clearFilters"
    />
    <div class="mt-5 bg-white rounded-xl border border-gray-200 overflow-hidden">
      <Table
        :columns="columns"
        :data="getIndex"
        :isSelectable="true"
        @newSelectedRows="updateSelectedRows"
        :selectableRowFilter="selectableRowFilter"
      >
        <template #paymentDate="{ row }">
          <span class="text-gray-600">{{ row.paymentDate }}</span>
        </template>
        <template #salesChannel="{ row }">
          <span class="font-medium">{{ row.salesChannel }}</span>
        </template>
        <template #transactionType="{ row }">
          <span
            class="text-xs font-medium ring-1 rounded px-1.5 py-0.5"
            :class="getTransactionTypeStyle(row.transactionType)"
            data-test="transaction-type"
          >
            {{ $t(`invoice.transaction_type.${row.transactionType}`) }}
          </span>
        </template>
        <template #friendlyId="{ row }">
          <span class="font-mono text-xs text-gray-500 whitespace-nowrap">{{ row.friendlyId }}</span>
        </template>
        <template #paymentStatus="{ row }">
          <span
            class="inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-2 py-0.5 ring-1"
            :class="getPaymentStatusStyle(row.paymentStatus).badge"
            data-test="payment-status"
          >
            <span class="w-1.5 h-1.5 rounded-full" :class="getPaymentStatusStyle(row.paymentStatus).dot"></span>
            {{ $t(`invoice.payment_status.${row.paymentStatus}`) }}
          </span>
        </template>
        <template #amount="{ row }">
          <span class="tabular-nums font-semibold whitespace-nowrap">{{ formatPrice(row.amount) }}</span>
        </template>
        <template #customButton="{ row }">
          <div class="flex justify-end gap-1 text-xs font-medium">
            <a
              v-if="row.fileUrl"
              :href="row.fileUrl"
              target="_blank"
              rel="noopener noreferrer"
              data-test="invoice-file-link"
              class="inline-flex items-center gap-1 px-1.5 py-1 rounded cursor-pointer text-gray-500 hover:bg-gray-100 transition-colors duration-150"
              @click.stop
            >
              <DownloadIcon class="h-3.5 w-3.5" />
              <span>PDF</span>
            </a>
            <a
              v-if="row.reportFileUrl"
              :href="row.reportFileUrl"
              target="_blank"
              rel="noopener noreferrer"
              data-test="report-file-link"
              class="inline-flex items-center gap-1 px-1.5 py-1 rounded cursor-pointer text-gray-500 hover:bg-gray-100 transition-colors duration-150"
              @click.stop
            >
              <DownloadIcon class="h-3.5 w-3.5" />
              <span>CSV</span>
            </a>
          </div>
        </template>
      </Table>
      <Pagination
        :currentPage="currentPage"
        :totalPages="getTotalPages"
        :totalCount="getTotalCount"
        @changePage="fetchInvoices"
      />
    </div>
    <InvoicesDownloadModal
      :open="openInvoicesDownloadModal"
      @close="openInvoicesDownloadModal = false"
      @sendInvoicesReport="sendReport"
    />
    <Modal :open="bulkTransferFormModalOpen" @close="bulkTransferFormModalOpen = false">
      <div class="p-4">
        <h2 class="text-lg font-bold">Exporter le fichier de virements aux marchands</h2>
        <p class="text-sm text-gray-500">Sélectionnez les données à exporter</p>
        <BulkTransferForm @success="bulkTransferFormModalOpen = false" />
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { PageHeader, Modal, Pagination, Table, SecondaryButton, formatPrice } from "@estaly/ui";
import { DownloadIcon } from "@heroicons/vue/outline";
import InvoicesDownloadModal from "@/components/billing/invoices/InvoicesDownloadModal.vue";
import BulkTransferForm from "@/components/billing/invoices/BulkTransferForm.vue";
import InvoicesSummary from "@/components/billing/invoices/InvoicesSummary.vue";
import InvoicesFilterBar from "@/components/billing/invoices/InvoicesFilterBar.vue";

const emptySelectedFilters = () => ({
  organizations: [],
  stores: [],
  billingEntities: [],
  paymentStatuses: [],
  transactionTypes: [],
  mandateStatuses: [],
  withSubjectUnpaidBill: false,
  paymentStartDate: null,
  paymentEndDate: null,
});

export default {
  components: {
    PageHeader,
    Table,
    Pagination,
    SecondaryButton,
    InvoicesDownloadModal,
    BulkTransferForm,
    InvoicesSummary,
    InvoicesFilterBar,
    Modal,
    DownloadIcon,
  },
  computed: {
    ...mapGetters("invoices", [
      "getTotalCount",
      "getTotalPages",
      "getIndex",
      "getPaymentStatuses",
      "getPaymentStatusStyle",
      "getTransactionTypeStyle",
      "getTransactionTypes",
      "getMandateStatuses",
    ]),
    ...mapState("organizations", { organizations: "index" }),
    ...mapState("stores", { stores: "index" }),
    ...mapGetters("billingEntities", ["billingEntities"]),

    storeOptions() {
      return this.stores.map((store) => store.salesChannel);
    },
    organizationOptions() {
      return this.organizations.map((organization) => organization.name);
    },
    billingEntityOptions() {
      return this.billingEntities.map((billingEntity) => billingEntity.name);
    },
  },
  data() {
    return {
      availableFilters: {
        organizations: [],
        stores: [],
        billingEntities: [],
        paymentStatuses: [],
        transactionTypes: [],
        mandateStatuses: [],
      },
      currentPage: 1,
      selectedRows: [],
      selectedFilters: emptySelectedFilters(),
      summary: null,
      exportingInvoices: false,
      openInvoicesDownloadModal: false,
      bulkTransferFormModalOpen: false,
      columns: [
        { label: "Date de versement", slot: "paymentDate", field: "paymentDate" },
        { label: "Entité", slot: "salesChannel", field: "salesChannel" },
        { label: "Type", slot: "transactionType", field: "transactionType" },
        { label: "Libellé", slot: "friendlyId", field: "friendlyId" },
        { label: "Montant", slot: "amount", field: "amount", numeric: true },
        { label: "Statut paiement", slot: "paymentStatus", field: "status" },
      ],
    };
  },
  async mounted() {
    await this.withLoader(async () => {
      await this.fetchOrganizationsIndex({ perPage: 2000 });
      await this.fetchStoresIndex({ invoiceResponsible: true, perPage: 2000 });
      await this.fetchBillingEntitiesIndex({ perPage: 2000, includes: [] });
    });

    this.availableFilters.paymentStatuses = this.getPaymentStatuses().map((paymentStatus) => ({
      label: this.$t(`invoice.payment_status.${paymentStatus}`),
      value: paymentStatus,
    }));

    this.availableFilters.transactionTypes = this.getTransactionTypes().map((transactionType) => ({
      label: this.$t(`invoice.transaction_type.${transactionType}`),
      value: transactionType,
    }));

    this.availableFilters.mandateStatuses = this.getMandateStatuses().map((mandateStatus) => ({
      label: this.$t(`invoice.mandate_status.${mandateStatus}`),
      value: mandateStatus,
    }));

    this.availableFilters.stores = this.storeOptions;
    this.availableFilters.organizations = this.organizationOptions;
    this.availableFilters.billingEntities = this.billingEntityOptions;

    this.fetchInvoices();
    this.refreshSummary();
  },
  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("invoices", ["fetchIndex", "fetchSummary", "sendInvoicesReport", "markInvoicesAsPaid"]),
    ...mapActions("stores", { fetchStoresIndex: "fetchIndex" }),
    ...mapActions("organizations", { fetchOrganizationsIndex: "fetchIndex" }),
    ...mapActions("billingEntities", { fetchBillingEntitiesIndex: "fetchIndex" }),
    ...mapActions("backoffice", ["withLoader"]),

    formatPrice,
    updateSelectedFilters(filterName, filterOptions) {
      this.selectedFilters[filterName] = filterOptions;
      this.fetchInvoices();
    },
    updateDateFilters(startDate, endDate) {
      this.selectedFilters["paymentStartDate"] = startDate;
      this.selectedFilters["paymentEndDate"] = endDate;
      this.fetchInvoices();
    },
    updateUnpaidBillFilter(withSubjectUnpaidBill) {
      this.selectedFilters.withSubjectUnpaidBill = withSubjectUnpaidBill;
      this.fetchInvoices();
    },
    clearFilters() {
      this.selectedFilters = emptySelectedFilters();
      this.fetchInvoices();
    },
    showBillsToRemind() {
      this.selectedFilters.transactionTypes = ["bill"];
      this.selectedFilters.paymentStatuses = ["pending_payment", "processing"];
      this.selectedFilters.mandateStatuses = ["not_signed"];
      this.fetchInvoices();
    },
    async fetchInvoices(page = 1) {
      this.currentPage = page;
      await this.withLoader(async () => {
        await this.fetchIndex({ page, ...this.selectedFilters });
      });
    },
    async markAsPaid() {
      const invoiceIds = this.selectedRows.map((row) => row.id);
      const response = await this.markInvoicesAsPaid(invoiceIds);
      if (response?.success) await Promise.all([this.fetchInvoices(), this.refreshSummary()]);
    },
    async refreshSummary() {
      this.summary = await this.fetchSummary();
    },
    async sendReport(email) {
      this.openInvoicesDownloadModal = false;

      const { success } = await this.sendInvoicesReport({
        email: email,
        paymentStartDate: this.selectedFilters["paymentStartDate"],
        paymentEndDate: this.selectedFilters["paymentEndDate"],
      });

      if (success) {
        this.notify({
          category: "simple",
          type: "success",
          title: "Generating the report, you should receive the e-mail in a few minutes.",
        });
      }
    },
    updateSelectedRows(newSelectedRows) {
      this.selectedRows = newSelectedRows;
    },
    selectableRowFilter(row) {
      return row.paymentStatus === "pending_payment";
    },
  },
};
</script>
